
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Message } from "element-ui";
import { cloneDeep, uniq } from "lodash";
import { IFactoryLoadingPlan } from "@/api/types";
import {
  completeContainerConfiguration,
  configContainers,
  removeConfiguredContainer,
  searchHqPackingList,
} from "@/api/factory-loading-plans";
import ConfigContainerPackingBlock from "./ConfigContainerPackingBlock.vue";
import {
  ContainerInfoModel,
  CustomsPackingItem,
  HqPackingListItem,
  isOverUnderageBlocked,
} from "./configContainerTypeModels";
import CustomsDeclarationPreviewDialog, {
  CustomsDeclarationPreviewItem,
} from "./CustomsDeclarationPreviewDialog.vue";
import PackingListProductListDialog, {
  PackingListProductItem,
} from "@/views/components/PackingListProductListDialog.vue";
import PiProductListDialog, {
  PiProductItem,
} from "@/views/components/PiProductListDialog.vue";
import {previewCustomsDeclaration} from "@/api/booking-order";
import { formatPrice, hasMoreThanTwoDecimals } from "@/utils/number";
import {detailQueryQtyStepInfoByPackingList, detailQueryQtyStepInfoByPi} from "@/api/hengan-loading-plan";

interface SearchParams {
  hqPackingListCode: string | null;
  factoryName: string | null;
  containerNumberKeyword: string | null;
}

@Component({
  name: "ConfigContainerTypeDialog",
  components: {
    ConfigContainerPackingBlock,
    CustomsDeclarationPreviewDialog,
    PackingListProductListDialog,
    PiProductListDialog,
  },
})
export default class ConfigContainerTypeDialog extends Vue {
  @Prop({ required: true }) private visible!: boolean;
  @Prop({ required: true }) private factoryLoadingPlan!: IFactoryLoadingPlan;

  private hasMoreThanTwoDecimals = hasMoreThanTwoDecimals;
  private isLoading = false;
  private packingInfo = new PackingInfoModel([], [], []);
  private unconfiguredList: HqPackingListItem[] = [];
  private configuredList: HqPackingListItem[] = [];
  private containerNumberList: string[] = [];

  private unconfiguredSearch: SearchParams = emptySearch();
  private configuredSearch: SearchParams = emptySearch();

  private configOptionPopoverVisible = false;
  private confirmPopoverVisible = false;
  private selectedContainerTypeId: string = null;
  private selectionTick = 0;
  /** 当前面板：未配置 / 已配置 */
  private activePanel: "unconfigured" | "configured" = "unconfigured";
  /** 装箱单展开状态，缺省为展开 */
  private unconfiguredExpandedMap: Record<string, boolean> = {};
  private configuredExpandedMap: Record<string, boolean> = {};

  private previewVisible = false;
  private previewLoading = false;
  private previewItems: CustomsDeclarationPreviewItem[] = [];

  private packingListProductVisible = false;
  private packingListProductLoading = false;
  private packingListProductItems: PackingListProductItem[] = [];

  private piProductVisible = false;
  private piProductLoading = false;
  private piProductItems: PiProductItem[] = [];

  @Watch("visible")
  private onVisibleChange(val: boolean) {
    if (val) {
      this.resetState();
      this.loadData();
    }
  }

  @Watch("unconfiguredSearch.containerNumberKeyword")
  private onUnconfiguredSealChange(sealKw: string) {
    this.applySealAutoSelect(sealKw);
    this.$nextTick(() => {
      this.selectionTick++;
    });
  }

  private resetState() {
    this.unconfiguredSearch = emptySearch();
    this.configuredSearch = emptySearch();
    this.configOptionPopoverVisible = false;
    this.confirmPopoverVisible = false;
    this.selectedContainerTypeId = null;
    this.packingInfo = new PackingInfoModel([], [], []);
    this.unconfiguredList = [];
    this.configuredList = [];
    this.containerNumberList = [];
    this.selectionTick = 0;
    this.activePanel = "unconfigured";
    this.unconfiguredExpandedMap = {};
    this.configuredExpandedMap = {};
  }

  private isBlockExpanded(
    section: "unconfigured" | "configured",
    id: string
  ): boolean {
    const map =
      section === "unconfigured"
        ? this.unconfiguredExpandedMap
        : this.configuredExpandedMap;
    return map[id] !== false;
  }

  private setBlockExpanded(
    section: "unconfigured" | "configured",
    id: string,
    expanded: boolean
  ) {
    const map =
      section === "unconfigured"
        ? this.unconfiguredExpandedMap
        : this.configuredExpandedMap;
    this.$set(map, id, expanded);
  }

  private setSectionExpanded(
    section: "unconfigured" | "configured",
    expanded: boolean
  ) {
    const list =
      section === "unconfigured"
        ? this.filteredUnconfiguredList
        : this.filteredConfiguredList;
    const next: Record<string, boolean> = {};
    list.forEach((hq) => {
      next[hq.id] = expanded;
    });
    if (section === "unconfigured") {
      this.unconfiguredExpandedMap = next;
    } else {
      this.configuredExpandedMap = next;
    }
  }

  private isSectionAllExpanded(
    section: "unconfigured" | "configured"
  ): boolean {
    const list =
      section === "unconfigured"
        ? this.filteredUnconfiguredList
        : this.filteredConfiguredList;
    if (!list.length) {
      return true;
    }
    return list.every((hq) => this.isBlockExpanded(section, hq.id));
  }

  private toggleSectionExpanded(section: "unconfigured" | "configured") {
    this.setSectionExpanded(section, !this.isSectionAllExpanded(section));
  }

  private get filteredUnconfiguredList(): HqPackingListItem[] {
    return this.filterHqList(this.unconfiguredList, this.unconfiguredSearch);
  }

  private get filteredConfiguredList(): HqPackingListItem[] {
    return this.filterHqList(this.configuredList, this.configuredSearch);
  }

  private get unconfiguredItemCount(): number {
    return countCustomsPackingItems(this.filteredUnconfiguredList);
  }

  private get configuredItemCount(): number {
    return countCustomsPackingItems(this.filteredConfiguredList);
  }

  private get unconfiguredTotalVolume(): string {
    void this.selectionTick;
    return sumSelectedField(this.filteredUnconfiguredList, "volume", true);
  }

  private get unconfiguredTotalGrossWeight(): string {
    void this.selectionTick;
    return sumSelectedField(this.filteredUnconfiguredList, "grossWeight", true);
  }

  private get unconfiguredTotalNetWeight(): string {
    void this.selectionTick;
    return sumSelectedField(this.filteredUnconfiguredList, "netWeight", true);
  }

  private get unconfiguredTotalPrice(): string {
    void this.selectionTick;
    return sumSelectedPrice(this.filteredUnconfiguredList, true);
  }

  private get configuredTotalVolume(): string {
    return sumSelectedField(this.filteredConfiguredList, "volume", false);
  }

  private get configuredTotalGrossWeight(): string {
    return sumSelectedField(this.filteredConfiguredList, "grossWeight", false);
  }

  private get configuredTotalNetWeight(): string {
    return sumSelectedField(this.filteredConfiguredList, "netWeight", false);
  }

  private get configuredTotalPrice(): string {
    return sumSelectedPrice(this.filteredConfiguredList, false);
  }

  private containerTypeOptionLabel(item: ContainerInfoModel): string {
    return `${item.name}-${item.containerTypeCode}-${
      item.loadingInOneOrMorePlaces ? "多地装柜" : "工厂拉柜"
    }${item.remark ? "-" + item.remark : ""}${
      item.containerNumber ? "-" + item.containerNumber : ""
    }`;
  }

  private filterHqList(
    list: HqPackingListItem[],
    search: SearchParams
  ): HqPackingListItem[] {
    const codeKw = (search.hqPackingListCode || "").trim().toLowerCase();
    const factoryKw = (search.factoryName || "").trim().toLowerCase();
    const sealKw = (search.containerNumberKeyword || "").trim();

    return list
      .map((hq) => {
        if (codeKw && !(hq.code || "").toLowerCase().includes(codeKw)) {
          return null;
        }
        if (
          factoryKw &&
          !(hq.factoryName || "").toLowerCase().includes(factoryKw)
        ) {
          return null;
        }
        if (!sealKw) {
          return hq;
        }
        const filteredItems: CustomsPackingItem[] = [];
        (hq.customsPackingItems || []).forEach((item) => {
          const children = (item.customsPackingChildernItems || []).filter(
            (c) => (c.containerNumber || "") === sealKw
          );
          if ((item.containerNumber || "") === sealKw) {
            filteredItems.push(item);
          } else if (children.length > 0) {
            filteredItems.push(...children);
          }
        });
        if (!filteredItems.length) {
          return null;
        }
        const view = Object.assign(new HqPackingListItem(), hq);
        view.customsPackingItems = filteredItems;
        return view;
      })
      .filter(Boolean) as HqPackingListItem[];
  }

  private applySealAutoSelect(sealKw: string) {
    const checked = !!(sealKw && String(sealKw).trim());
    this.filteredUnconfiguredList.forEach((hq) => {
      (hq.customsPackingItems || []).forEach((item) => {
        const next = checked && !isOverUnderageBlocked(item.overUnderageStatus);
        this.$set(item, "select", next);
        if (item.customsPackingChildernItems) {
          item.customsPackingChildernItems.forEach((c) => {
            this.$set(
              c,
              "select",
              checked && !isOverUnderageBlocked(c.overUnderageStatus)
            );
          });
        }
      });
    });
    this.selectionTick++;
  }

  private onPackingSelectionChange() {
    this.selectionTick++;
  }

  private async loadData() {
    if (!this.factoryLoadingPlan || !this.factoryLoadingPlan.id) {
      return;
    }
    this.isLoading = true;
    try {
      const {
        hqPackingListItems,
        containerTypeList,
        configuredContainers,
      }: any = await searchHqPackingList(this.factoryLoadingPlan.id, {
        hqPackingListCode: null,
        factoryName: null,
        containerNumberKeyword: null,
      });
      const hqPackingLists: HqPackingListItem[] = (hqPackingListItems || []).map(
        (value: HqPackingListItem) => {
          const item = new HqPackingListItem();
          Object.assign(item, value);
          item.customsPackingItems = (value.customsPackingItems || []).map(
            (c) => ({ select: false, ...c })
          );
          return item;
        }
      );
      this.packingInfo = new PackingInfoModel(
        containerTypeList || [],
        configuredContainers || [],
        hqPackingLists
      );
      this.rebuildSplitLists();
      this.buildContainerNumberList();
      if (this.unconfiguredSearch.containerNumberKeyword) {
        this.applySealAutoSelect(this.unconfiguredSearch.containerNumberKeyword);
      }
    } finally {
      setTimeout(() => {
        this.isLoading = false;
      }, 300);
    }
  }

  private rebuildSplitLists() {
    this.unconfiguredList = splitByConfigured(
      this.packingInfo.hqPackingListItems,
      false
    );
    this.configuredList = splitByConfigured(
      this.packingInfo.hqPackingListItems,
      true
    );
  }

  private buildContainerNumberList() {
    const numbers: string[] = [];
    this.packingInfo.hqPackingListItems.forEach((hq) => {
      (hq.customsPackingItems || []).forEach((item) => {
        if (item.containerNumber) {
          numbers.push(item.containerNumber);
        }
        (item.customsPackingChildernItems || []).forEach((child) => {
          if (child.containerNumber) {
            numbers.push(child.containerNumber);
          }
        });
      });
    });
    this.containerNumberList = uniq(numbers).filter((s) => s && s.trim());
  }

  private getSelectedCustomsPackingItems(): CustomsPackingItem[] {
    const items: CustomsPackingItem[] = [];
    this.filteredUnconfiguredList.forEach((hq) => {
      (hq.customsPackingItems || []).forEach((item) => {
        items.push(item);
        if (item.customsPackingChildernItems) {
          items.push(...item.customsPackingChildernItems);
        }
      });
    });
    return items.filter(
      (c) =>
        c.select &&
        c.configuredContainerId === null &&
        !isOverUnderageBlocked(c.overUnderageStatus)
    );
  }

  private getSelectedConfiguredPackingItems(): CustomsPackingItem[] {
    const items: CustomsPackingItem[] = [];
    this.filteredConfiguredList.forEach((hq) => {
      (hq.customsPackingItems || []).forEach((item) => {
        items.push(item);
        if (item.customsPackingChildernItems) {
          items.push(...item.customsPackingChildernItems);
        }
      });
    });
    return items.filter((c) => c.select);
  }

  private get hasConfiguredSelection(): boolean {
    void this.selectionTick;
    return this.getSelectedConfiguredPackingItems().length > 0;
  }

  private async onRemoveConfigured() {
    const selected = this.getSelectedConfiguredPackingItems();
    if (selected.length < 1) {
      Message.error({ message: "请选择要移除的主包装", duration: 2000 });
      return;
    }
    try {
      await this.$confirm(
        `确定将已选的${selected.length}个主包装从柜型中移除？`,
        "提示",
        { type: "warning" }
      );
    } catch {
      return;
    }
    await removeConfiguredContainer(
      this.factoryLoadingPlan.id,
      uniq(selected.map((c) => c.id).filter(Boolean))
    );
    Message.success({ message: "移除成功", duration: 2000 });
    await this.loadData();
  }

  private isLoadingOnMultiPlace(containerTypeId: string): boolean {
    const found = this.packingInfo.containerTypeList.filter(
      (value) => value.id === containerTypeId
    )[0];
    return found ? found.loadingInOneOrMorePlaces : false;
  }

  private async onContainerTypeConfigured() {
    this.configOptionPopoverVisible = false;
    const customsPackingSet = this.getSelectedCustomsPackingItems();
    if (customsPackingSet.length < 1) {
      Message.error({ message: "请选择主包装", duration: 2000 });
      return;
    }
    if (!this.isLoadingOnMultiPlace(this.selectedContainerTypeId)) {
      const hqIds = uniq(
        customsPackingSet.map((value) => value.hqPackingListId)
      );
      if (hqIds.length > 1) {
        Message.error({
          message: "工厂拉柜的柜型不能跨装箱单",
          duration: 2000,
        });
        return;
      }
    }
    const data = customsPackingSet.map((c) => ({
      containerTypeId: this.selectedContainerTypeId,
      customsPackingId: c.id,
    }));
    await configContainers(this.factoryLoadingPlan.id, data);
    this.selectedContainerTypeId = null;
    await this.loadData();
  }

  private async completeContainerConfiguration() {
    const loading = this.$loading({
      lock: true,
      text: "加载中",
      spinner: "el-icon-loading",
      background: "rgba(0, 0, 0, 0.7)",
    });
    if (this.packingInfo.configuredContainers.length < 1) {
      loading.close();
      Message.error({ message: "请先配置柜型", duration: 2000 });
      return;
    }
    try {
      await completeContainerConfiguration(this.factoryLoadingPlan.id);
      this.confirmPopoverVisible = false;
      loading.close();
      this.$emit("update:visible", false);
      this.$emit("success");
    } catch (err) {
      console.log(err);
      loading.close();
    }
  }

  private handleClose() {
    this.configOptionPopoverVisible = false;
    this.confirmPopoverVisible = false;
    this.$emit("update:visible", false);
    this.$emit("closed");
  }

  private async declarationPreview() {
    const data = {
      customerId: this.factoryLoadingPlan.customerId,
      factoryPlanIds: [this.factoryLoadingPlan.id],
    }

    this.previewItems = [];
    this.previewVisible = true;
    this.previewLoading = true;
    try {
      this.previewItems = await previewCustomsDeclaration(data) as any;
    } finally {
      this.previewLoading = false;
    }
  }

  private async showPackingListProductList() {
    this.packingListProductItems = [];
    this.packingListProductVisible = true;
    this.packingListProductLoading = true;
    try {
      const cIds = this.filteredConfiguredList.flatMap(item => item.customsPackingItems.map(i => i.id));
      this.packingListProductItems = await detailQueryQtyStepInfoByPackingList({ packingIds: cIds }) as any;
    } finally {
      this.packingListProductLoading = false;
    }
  }

  private async showPiProductList() {
    this.piProductItems = [];
    this.piProductVisible = true;
    this.piProductLoading = true;
    try {
      const cIds = this.filteredConfiguredList.flatMap(item => item.customsPackingItems.map(i => i.id));
      this.piProductItems = await detailQueryQtyStepInfoByPi({ packingIds: cIds }) as any;
    } finally {
      this.piProductLoading = false;
    }
  }
}

function emptySearch(): SearchParams {
  return {
    hqPackingListCode: null,
    factoryName: null,
    containerNumberKeyword: null,
  };
}

function isConfigured(item: CustomsPackingItem): boolean {
  return item.configuredContainerId != null;
}

function splitByConfigured(
  hqPackingListItems: HqPackingListItem[],
  configured: boolean
): HqPackingListItem[] {
  const result: HqPackingListItem[] = [];
  hqPackingListItems.forEach((hq) => {
    const items: CustomsPackingItem[] = [];
    (hq.customsPackingItems || []).forEach((item) => {
      const children = (item.customsPackingChildernItems || []).filter(
        (c) => isConfigured(c) === configured
      );
      const selfMatch = isConfigured(item) === configured;
      if (selfMatch) {
        const node = cloneDeep(item);
        if (item.customsPackingChildernItems) {
          node.customsPackingChildernItems = cloneDeep(children);
        }
        items.push(node);
      } else if (children.length > 0) {
        children.forEach((c) => items.push(cloneDeep(c)));
      }
    });
    if (items.length > 0) {
      const hqClone = cloneDeep(hq);
      hqClone.customsPackingItems = items;
      result.push(hqClone);
    }
  });
  return result;
}

function flattenCustomsPackingItems(
  list: HqPackingListItem[]
): CustomsPackingItem[] {
  const items: CustomsPackingItem[] = [];
  list.forEach((hq) => {
    (hq.customsPackingItems || []).forEach((item) => {
      items.push(item);
      if (item.customsPackingChildernItems) {
        items.push(...item.customsPackingChildernItems);
      }
    });
  });
  return items;
}

function countCustomsPackingItems(list: HqPackingListItem[]): number {
  return flattenCustomsPackingItems(list).length;
}

function sumSelectedField(
  list: HqPackingListItem[],
  field: "volume" | "grossWeight" | "netWeight",
  selectedOnly: boolean
): string {
  const items = flattenCustomsPackingItems(list).filter((c) =>
    selectedOnly ? c.select : true
  );
  return items
    .map((value) => value[field])
    .reduce((pre, cur) => pre + Number(cur || 0), 0)
    .toFixed(2);
}

function packingItemPrice(item: CustomsPackingItem): number {
  return (item.productInfoItems || [])
    .map((p) => Number(p.unitPrice || 0) * Number(p.totalNumberPcs || 0))
    .reduce((pre, cur) => pre + cur, 0);
}

function sumSelectedPrice(
  list: HqPackingListItem[],
  selectedOnly: boolean
): string {
  const items = flattenCustomsPackingItems(list).filter((c) =>
    selectedOnly ? c.select : true
  );
  const total = items
    .map((item) => packingItemPrice(item))
    .reduce((pre, cur) => pre + cur, 0);
  return formatPrice(total);
}

class PackingInfoModel {
  containerTypeList: ContainerInfoModel[] = [];
  configuredContainers: ContainerInfoModel[] = [];
  hqPackingListItems: HqPackingListItem[] = [];

  constructor(
    containerTypeList: ContainerInfoModel[],
    configuredContainers: ContainerInfoModel[],
    hqPackingListItems: HqPackingListItem[]
  ) {
    this.containerTypeList = containerTypeList;
    this.configuredContainers = configuredContainers;
    this.hqPackingListItems = hqPackingListItems;
  }
}
